import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FC } from 'react'
import type { Word } from '../../types/wordCollection'
import { createWord } from '../../types/wordCollection'
import { useWordCollection } from '../../hooks/useWordCollection'
import { useRhymeGrouping } from '../../hooks/useRhymeGrouping'
import { RhymeCategory } from '../../utils/rhyme/types'
import type { RhymeGroup } from '../../types/wordCollection'
import SidebarHeader from './SidebarHeader'
import CollectionSearch from './CollectionSearch'
import { QuickAddInput } from './QuickAddInput'
import type { QuickAddResult } from './QuickAddInput'
import { RhymeGroupCluster } from './RhymeGroupCluster'
import { RhymeColorLegend } from './RhymeColorLegend'
import { SetAsideList } from './SetAsideList'
import { SetAsideReasonModal } from './SetAsideReasonModal'

import type { WorksheetOpenOptions } from '../../hooks/useWorksheetState'

interface WordCollectionSidebarProps {
  songId: string
  onExploreWords?: () => void
  onOpenWorksheet?: (options?: WorksheetOpenOptions) => void
  onSettings?: () => void
}

const SECTION_LOOKUP = [
  {
    status: 'collected',
    title: 'Collected',
    headerTextClass: 'text-green-700',
    headerBgClass: 'bg-green-50/50'
  },
  {
    status: 'set_aside',
    title: 'Set Aside',
    headerTextClass: 'text-gray-500',
    headerBgClass: 'bg-white'
  }
]

/**
 * Derive cluster accent color from the group's dominant category.
 * Accepts the string category from the backend or the RhymeCategory enum —
 * both end up here after the hook normalizes to enum values.
 */
function getClusterAccentColor(group: RhymeGroup): string {
  const COLOR_HEX: Record<string, string> = {
    [RhymeCategory.PERFECT]:         '#22c55e',
    [RhymeCategory.FAMILY_VOICED]:   '#3b82f6',
    [RhymeCategory.FAMILY_UNVOICED]: '#3b82f6',
    [RhymeCategory.ADDITIVE]:        '#06b6d4',
    [RhymeCategory.SUBTRACTIVE]:     '#eab308',
    [RhymeCategory.ASSONANCE]:       '#f97316',
    [RhymeCategory.CONSONANCE]:      '#a855f7',
    [RhymeCategory.NONE]:            '#d6d3d1',
  }

  const hex = COLOR_HEX[group.dominantCategory] ?? '#d6d3d1'
  return hex
}

export const WordCollectionSidebar: FC<WordCollectionSidebarProps> = ({
  songId,
  onExploreWords,
  onOpenWorksheet,
  onSettings
}) => {
  const {
    words,
    collectedWords,
    setAsideWords,
    searchQuery,
    setSearchQuery,
    usedWordsInLyrics,
    isLoading,
    addWord,
    deleteWord,
    changeWordStatus,
    updateWordNotes,
  } = useWordCollection(songId)

  // ---------------------------------------------------------------------------
  // Key word overrides — persisted per-song in localStorage.
  // Format (Scope 13): wordId → preferredKeyWordId.
  // Storing one entry per group (the new key word maps to itself) is sufficient
  // because the backend scans overrides and honors the first match per group.
  // ---------------------------------------------------------------------------

  const [keyWordOverrides, setKeyWordOverrides] = useState<Record<string, string>>({})

  // Load overrides on mount and whenever songId changes
  useEffect(() => {
    const stored = localStorage.getItem(`keyWordOverrides:${songId}`)
    if (stored) {
      try {
        setKeyWordOverrides(JSON.parse(stored))
      } catch {
        // Corrupted — start fresh rather than crash
        setKeyWordOverrides({})
      }
    } else {
      setKeyWordOverrides({})
    }
  }, [songId])

  // Persist to localStorage whenever overrides change
  useEffect(() => {
    localStorage.setItem(`keyWordOverrides:${songId}`, JSON.stringify(keyWordOverrides))
  }, [songId, keyWordOverrides])

  // Compute rhyme groups via backend API — debounced, race-safe, no local fallback
  const {
    groups: rhymeGroups,
    isComputing: isGrouping,
    backendAvailable,
    optimisticWordIds,
    wordCategoryMaps,
    recategorizeGroup: recategorizeGroupApi,
    categorizeNewWords,
    refreshCollection,
  } = useRhymeGrouping(collectedWords, keyWordOverrides, songId)

  // Fast lookup: lowercase word text → Word object — avoids O(n) scans in render
  const wordLookup = useMemo(
    () => new Map(collectedWords.map(w => [w.word.toLowerCase(), w])),
    [collectedWords]
  )

  // Word ID → Word object lookup for key word resolution
  const wordIdLookup = useMemo(
    () => new Map(collectedWords.map(w => [w.id, w])),
    [collectedWords]
  )

  const [searchValue, setSearchValue] = useState(searchQuery ?? '')
  const [reasonModalOpen, setReasonModalOpen] = useState(false)
  const [wordToSetAside, setWordToSetAside] = useState<Word | null>(null)

  /**
   * Resolve the effective key word ID for a group.
   * Checks overrides (by word ID) before defaulting to first in the group.
   * Singletons have no key word concept.
   */
  const getKeyWordId = useCallback(
    (_group: RhymeGroup, groupWords: Word[]): string | null => {
      if (groupWords.length <= 1) return null

      // Check if any word in this group has an override pointing to a valid key word
      for (const w of groupWords) {
        const override = keyWordOverrides[w.id]
        if (override && groupWords.some(gw => gw.id === override)) {
          return override
        }
      }

      // Default: first word in the resolved group list
      return groupWords[0]?.id ?? null
    },
    [keyWordOverrides]
  )

  const handleMakeKeyWord = useCallback((groupPhoneticKey: string, newKeyWordId: string) => {
    // Store: newKeyWordId → newKeyWordId (one entry per group is sufficient)
    setKeyWordOverrides(prev => ({ ...prev, [newKeyWordId]: newKeyWordId }))

    // Notify the hook so it recategorizes relative to the new key word
    const group = rhymeGroups.find(g => g.phoneticKey === groupPhoneticKey)
    if (!group) return

    const groupWords = group.words
      .map(w => wordLookup.get(w.toLowerCase()))
      .filter((w): w is Word => w !== undefined)

    const keyWordObj = wordIdLookup.get(newKeyWordId)
    if (!keyWordObj) return

    recategorizeGroupApi(groupPhoneticKey, newKeyWordId, keyWordObj.word, groupWords)
  }, [rhymeGroups, wordLookup, wordIdLookup, recategorizeGroupApi])

  // ---------------------------------------------------------------------------
  // Search and filter
  // ---------------------------------------------------------------------------

  useEffect(() => {
    setSearchValue(searchQuery ?? '')
  }, [searchQuery])

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(searchValue.trim())
    }, 150)

    return () => clearTimeout(handler)
  }, [searchValue, setSearchQuery])

  const normalizedFilter = useMemo(() => searchValue.trim().toLowerCase(), [searchValue])

  const handleClearSearch = useCallback(() => {
    setSearchValue('')
    setSearchQuery('')
  }, [setSearchQuery])

  // ---------------------------------------------------------------------------
  // Word actions
  // ---------------------------------------------------------------------------

  const handleQuickAdd = useCallback(
    (wordText: string): QuickAddResult => {
      // Case-insensitive duplicate check across ALL words (collected + set_aside)
      const normalized = wordText.toLowerCase()
      const exists = words.some(w => w.word.toLowerCase() === normalized)

      if (exists) {
        return { success: false, reason: 'duplicate' }
      }

      const newWord = createWord({
        id: crypto.randomUUID(),
        songId,
        word: wordText,
        status: 'collected',
        discovery: { method: 'user_brainstorm' }
      })

      addWord(newWord)
      // Quick-add is the "eligible simple case" — incremental categorization
      // rather than a full collection reprocess. categorizeNewWords handles
      // optimistic UI and triggers a full refresh internally once it resolves.
      categorizeNewWords(wordText, [wordText.toLowerCase()])
      return { success: true }
    },
    [words, songId, addWord, categorizeNewWords]
  )

  const handleExploreRhymes = useCallback((word: string, wordId?: string) => {
    if (onOpenWorksheet) {
      onOpenWorksheet({
        searchTerm: word,
        tab: 'rhymes',
        autoSearch: true,
        parentWordId: wordId
      })
    } else {
      console.log('Explore rhymes for:', word)
    }
  }, [onOpenWorksheet])

  const handleExploreThesaurus = useCallback((word: string, wordId?: string) => {
    if (onOpenWorksheet) {
      onOpenWorksheet({
        searchTerm: word,
        tab: 'thesaurus',
        autoSearch: true,
        parentWordId: wordId
      })
    } else {
      console.log('Explore thesaurus for:', word)
    }
  }, [onOpenWorksheet])

  const handleUpdateNotes = useCallback((wordId: string, notes: string) => {
    updateWordNotes(wordId, notes)
  }, [updateWordNotes])

  const handleRequestSetAside = useCallback((word: Word) => {
    setWordToSetAside(word)
    setReasonModalOpen(true)
  }, [])

  const handleSetAsideConfirm = useCallback((reason?: string) => {
    if (wordToSetAside) {
      changeWordStatus(wordToSetAside.id, 'set_aside', reason)
      // After set-aside removes a word from the group, refresh so backend recomputes
      refreshCollection()
    }
    setReasonModalOpen(false)
    setWordToSetAside(null)
  }, [wordToSetAside, changeWordStatus, refreshCollection])

  const handleSetAsideCancel = useCallback(() => {
    setReasonModalOpen(false)
    setWordToSetAside(null)
  }, [])

  const handleReactivate = useCallback((wordId: string) => {
    changeWordStatus(wordId, 'collected')
    // Reactivation returns a word to the collection — trigger a fresh categorization
    refreshCollection()
  }, [changeWordStatus, refreshCollection])

  const filterWords = useCallback(
    (list: Word[]) => {
      if (!normalizedFilter) {
        return list
      }

      return list.filter(word => word.word.toLowerCase().includes(normalizedFilter))
    },
    [normalizedFilter]
  )

  const clearCollection = () => {
    words.forEach(word => {
      deleteWord(word.id)
    })
    handleClearSearch()
  }

  const totalWordCount = collectedWords.length + setAsideWords.length

  const getWordsByStatus = useCallback(
    (status: string) => {
      if (status === 'collected') return collectedWords
      return setAsideWords
    },
    [collectedWords, setAsideWords]
  )

  const sections = useMemo(
    () =>
      SECTION_LOOKUP.map(config => ({
        key: config.status,
        words: filterWords(getWordsByStatus(config.status)),
        ...config
      })),
    [filterWords, getWordsByStatus]
  )

  return (
    <div
      className="hidden h-full flex-none w-80 flex-col rounded-2xl border border-gray-200 bg-white/90 shadow-lg backdrop-blur-md lg:flex"
      data-testid="word-collection-sidebar"
      data-loaded={!isLoading ? 'true' : 'false'}
    >
      <div className="flex h-full flex-col">
        <SidebarHeader
          wordCount={totalWordCount}
          onClear={clearCollection}
          onSettings={onSettings}
        />

        <QuickAddInput onAdd={handleQuickAdd} />

        <CollectionSearch
          value={searchValue}
          onChange={setSearchValue}
          onClear={handleClearSearch}
        />

        {/* Backend unavailable banner — shown when the categorization service is down */}
        {!backendAvailable && (
          <div
            className="mx-4 mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
            data-testid="rhyme-analysis-unavailable-banner"
            role="alert"
          >
            <p className="text-[0.65rem] font-medium text-amber-700">
              rhyme analysis unavailable
            </p>
            <p className="text-[0.6rem] text-amber-600">
              Groups will update when the service reconnects.
            </p>
          </div>
        )}

        <div className="flex flex-1 flex-col gap-3 overflow-y-auto overflow-x-hidden px-4 pb-4">
          {sections.map(section => {
            // Collected section: grouped display via useRhymeGrouping output
            if (section.key === 'collected') {
              // Filter rhyme groups to only include words matching the search query
              const filteredGroups = normalizedFilter
                ? rhymeGroups
                    .map(group => ({
                      ...group,
                      words: group.words.filter(w => w.includes(normalizedFilter))
                    }))
                    .filter(group => group.words.length > 0)
                : rhymeGroups

              // Separate multi-word groups from singletons for V6 clustering
              const multiWordGroups = filteredGroups.filter(g => g.words.length > 1)
              const singletons = filteredGroups.filter(g => g.words.length === 1)

              return (
                <div
                  key={section.key}
                  className="rounded-2xl border border-gray-200 bg-white shadow-sm"
                  data-testid="state-section-collected"
                >
                  {/* Section header with color legend */}
                  <div className={`px-4 py-3 ${section.headerBgClass}`}>
                    <p className={`text-xs font-semibold uppercase tracking-[0.15em] ${section.headerTextClass}`}>
                      {section.title}
                    </p>
                    <p className="text-[0.65rem] text-gray-500">
                      {section.words.length} word{section.words.length === 1 ? '' : 's'}
                    </p>
                    <div className="mt-2">
                      <RhymeColorLegend />
                    </div>
                  </div>

                  {/* Dense pill flow — V6 clustering layout */}
                  <div className="px-3 pb-3 pt-2">
                    {section.words.length === 0 ? (
                      <p className="text-[0.65rem] text-gray-400">No words yet</p>
                    ) : isGrouping ? (
                      // Brief spinner while grouping computes — 300ms debounce so rarely flickers
                      <p className="text-[0.65rem] text-gray-400 animate-pulse">Grouping...</p>
                    ) : filteredGroups.length === 0 ? (
                      <p className="text-[0.65rem] text-gray-400">No matching words</p>
                    ) : (
                      <div
                        className="flex flex-wrap items-start"
                        style={{ gap: '8px' }}
                        data-testid="dense-pill-flow"
                      >
                        {/* Multi-word groups: each in a cluster wrapper with accent border */}
                        {multiWordGroups.map(group => {
                          const groupWords = group.words
                            .map(w => wordLookup.get(w.toLowerCase()))
                            .filter((w): w is Word => w !== undefined)

                          if (groupWords.length === 0) return null

                          const keyWordId = getKeyWordId(group, groupWords)
                          // Pull per-word categories from the hook's map (set by backend)
                          const groupPillCategories = wordCategoryMaps.get(group.phoneticKey) ?? new Map<string, RhymeCategory>()
                          const accentColor = getClusterAccentColor(group)

                          return (
                            <div
                              key={group.phoneticKey}
                              className="inline-flex flex-wrap"
                              style={{
                                gap: '3px',
                                padding: '3px 4px',
                                borderRadius: '4px',
                                background: '#fafaf9',
                                borderLeft: `2px solid ${accentColor}4d`, // ~30% opacity via hex alpha
                              }}
                              data-testid={`rhyme-cluster-${group.phoneticKey}`}
                            >
                              <RhymeGroupCluster
                                key={group.phoneticKey}
                                group={group}
                                words={groupWords}
                                usedWordsInLyrics={usedWordsInLyrics}
                                keyWordId={keyWordId}
                                wordCategories={groupPillCategories}
                                onSetAside={handleRequestSetAside}
                                onExploreRhymes={handleExploreRhymes}
                                onExploreThesaurus={handleExploreThesaurus}
                                onUpdateNotes={handleUpdateNotes}
                                onMakeKeyWord={handleMakeKeyWord}
                              />
                            </div>
                          )
                        })}

                        {/* Singleton section with "Unrhymed" label separator */}
                        {singletons.length > 0 && (
                          <>
                            {/* Flex break + label only when there are also multi-word groups */}
                            {multiWordGroups.length > 0 && (
                              <div style={{ flexBasis: '100%', height: 0, margin: '2px 0' }} />
                            )}
                            {multiWordGroups.length > 0 && (
                              <div
                                style={{
                                  flexBasis: '100%',
                                  fontSize: '9px',
                                  fontWeight: 500,
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase',
                                  color: '#a8a29e',
                                  marginBottom: '2px',
                                }}
                                data-testid="unrhymed-label"
                              >
                                Unrhymed
                              </div>
                            )}
                            {singletons.map(group => {
                              const groupWords = group.words
                                .map(w => wordLookup.get(w.toLowerCase()))
                                .filter((w): w is Word => w !== undefined)

                              if (groupWords.length === 0) return null

                              // Show optimistic placeholder if this word is being computed
                              const wordObj = groupWords[0]
                              const isOptimistic = wordObj && optimisticWordIds.has(wordObj.id)

                              if (isOptimistic) {
                                return (
                                  <span
                                    key={group.phoneticKey}
                                    className="animate-pulse text-[0.6rem] text-gray-400 italic"
                                    data-testid={`optimistic-placeholder-${wordObj.id}`}
                                  >
                                    {wordObj.word} (computing...)
                                  </span>
                                )
                              }

                              return (
                                <RhymeGroupCluster
                                  key={group.phoneticKey}
                                  group={group}
                                  words={groupWords}
                                  usedWordsInLyrics={usedWordsInLyrics}
                                  keyWordId={null}
                                  wordCategories={new Map()}
                                  onSetAside={handleRequestSetAside}
                                  onExploreRhymes={handleExploreRhymes}
                                  onExploreThesaurus={handleExploreThesaurus}
                                  onUpdateNotes={handleUpdateNotes}
                                  onMakeKeyWord={handleMakeKeyWord}
                                />
                              )
                            })}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            }

            // Special rendering for set_aside section with SetAsideList
            if (section.key === 'set_aside') {
              return (
                <SetAsideList
                  key={section.key}
                  words={section.words}
                  searchQuery={normalizedFilter}
                  onReactivate={handleReactivate}
                  onUpdateNotes={handleUpdateNotes}
                />
              )
            }

            // Unreachable with current SECTION_LOOKUP config, but TypeScript needs a return
            return null
          })}
        </div>

        <div className="px-4 pb-4 pt-2">
          <button
            type="button"
            onClick={() => (onOpenWorksheet ? onOpenWorksheet() : onExploreWords?.())}
            className="flex h-11 w-full items-center justify-center rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 text-sm font-semibold uppercase tracking-[0.2em] text-white shadow-lg transition hover:from-green-600 hover:to-emerald-700"
            data-testid="word-collection-explore"
          >
            Explore Words
          </button>
        </div>
      </div>

      {/* Set Aside Reason Modal */}
      <SetAsideReasonModal
        isOpen={reasonModalOpen}
        wordLabel={wordToSetAside?.word || ''}
        onConfirm={handleSetAsideConfirm}
        onCancel={handleSetAsideCancel}
      />
    </div>
  )
}

export default WordCollectionSidebar
